// Aba "Entregáveis" do contrato (Onda Op-3a).
// Wave 2 (Antd) — 2026-06-01.

import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Tag, Table, Form, Input, InputNumber, DatePicker, Modal, Checkbox,
  Empty, Typography, Space, Flex, Popconfirm, App as AntApp, Spin, Statistic, Row, Col, Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, RollbackOutlined,
  LinkOutlined, FileTextOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { fmt, fmtData, num, getToken } from './utils';
import type { Contrato } from './types';

const { TextArea } = Input;

const STATUS_ENTREGAVEL: Record<string, { label: string; cor: string; bg: string }> = {
  pendente:           { label: 'Pendente',        cor: '#888',    bg: '#F2F2F4' },
  entregue:           { label: 'Entregue',        cor: '#0a7f4a', bg: '#E8F5EE' },
  a_medir:            { label: 'A medir',         cor: '#B07500', bg: '#FFF8E8' },
  bm_enviado:         { label: 'BM enviado',      cor: '#0930D0', bg: '#EFF3FF' },
  aprovado_cliente:   { label: 'Aprovado cliente', cor: '#7C3AED', bg: '#F3E8FF' },
  faturado:           { label: 'Faturado',        cor: '#020D7F', bg: '#E4E7F1' },
  cancelado:          { label: 'Cancelado',       cor: '#888',    bg: '#F2F2F4' },
};

export function AbaEntregaveis({ contrato, onAtualizar }: { contrato: Contrato; onAtualizar: () => void }) {
  return <AntApp><AbaEntregaveisInner contrato={contrato} onAtualizar={onAtualizar} /></AntApp>;
}

function AbaEntregaveisInner({ contrato, onAtualizar }: { contrato: Contrato; onAtualizar: () => void }) {
  const { message, modal } = AntApp.useApp();
  const entregaveis = contrato.entregaveis || [];
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  const [form, setForm] = useState<any>({
    descricao: '', valor: null, responsavel_email: '', data_prevista: '', observacoes: '',
  });
  const [salvando, setSalvando] = useState(false);
  const [vinculando, setVinculando] = useState<{ id: string; descricao: string } | null>(null);

  const valorTotal = entregaveis.reduce((s, e) => s + (e.valor || 0), 0);
  const valorEntregue = entregaveis
    .filter(e => ['entregue','a_medir','bm_enviado','aprovado_cliente','faturado'].includes(e.status))
    .reduce((s, e) => s + (e.valor || 0), 0);
  const valorPendente = valorTotal - valorEntregue;
  const valorContratado = num(contrato.valor_contratado) + num(contrato.aditivos_valor_soma);
  const pctEntregue = valorTotal > 0 ? (valorEntregue / valorTotal) * 100 : 0;

  function abrirCriar() {
    setEditando(null);
    setForm({ descricao: '', valor: null, responsavel_email: '', data_prevista: '', observacoes: '' });
    setCriando(true);
  }
  function abrirEditar(e: any) {
    setEditando(e.id);
    setForm({
      descricao: e.descricao || '',
      valor: e.valor ?? null,
      responsavel_email: e.responsavel_email || '',
      data_prevista: e.data_prevista || '',
      observacoes: e.observacoes || '',
    });
    setCriando(true);
  }

  async function salvar() {
    if (!form.descricao.trim()) { message.warning('Descrição é obrigatória'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const payload: any = {
        descricao: form.descricao,
        valor: form.valor != null ? form.valor : null,
        responsavel_email: form.responsavel_email || null,
        data_prevista: form.data_prevista || null,
        observacoes: form.observacoes || null,
      };
      if (editando) {
        await axios.post(`${API_URL}/comercial/propostas-geradas`, {
          action: 'op_atualizar_entregavel', id: editando, ...payload,
        }, { headers: { Authorization: token } });
      } else {
        await axios.post(`${API_URL}/comercial/propostas-geradas`, {
          action: 'op_criar_entregavel', contrato_id: contrato.id, ...payload,
        }, { headers: { Authorization: token } });
      }
      message.success(editando ? 'Entregável atualizado.' : 'Entregável criado.');
      setCriando(false);
      setEditando(null);
      onAtualizar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function excluir(e: any) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'op_excluir_entregavel', id: e.id,
      }, { headers: { Authorization: token } });
      message.success('Excluído.');
      onAtualizar();
    } catch (err: any) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    }
  }

  async function marcarEntregue(e: any) {
    const proceder = async () => {
      try {
        const token = await getToken();
        await axios.post(`${API_URL}/comercial/propostas-geradas`, {
          action: 'op_marcar_entregue', id: e.id,
        }, { headers: { Authorization: token } });
        message.success('Marcado como entregue.');
        onAtualizar();
      } catch (err: any) {
        message.error('Erro: ' + (err.response?.data?.error || err.message));
      }
    };
    if (!e.relatorio_s3_key) {
      modal.confirm({
        title: 'Sem relatório anexado',
        content: 'Marcar como entregue mesmo sem relatório?',
        onOk: proceder,
      });
    } else {
      proceder();
    }
  }

  async function desmarcarEntregue(e: any) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'op_desmarcar_entregue', id: e.id,
      }, { headers: { Authorization: token } });
      message.success('Entrega revertida.');
      onAtualizar();
    } catch (err: any) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    }
  }

  const columns: ColumnsType<NonNullable<Contrato['entregaveis']>[number]> = [
    { title: '#', dataIndex: 'numero_seq', width: 40, align: 'center',
      render: (n) => <Typography.Text strong style={{ color: '#020D7F' }}>{n || '—'}</Typography.Text> },
    { title: 'Descrição', key: 'desc',
      render: (_, e) => (
        <div>
          <Typography.Text strong>{e.descricao}</Typography.Text>
          {e.observacoes && <div><Typography.Text type="secondary" style={{ fontSize: 10 }}>{e.observacoes}</Typography.Text></div>}
          {e.relatorio_s3_key && (
            <div><Typography.Text style={{ fontSize: 10, color: '#0930D0' }}>
              <FileTextOutlined /> {e.relatorio_nome || 'relatório anexado'}
            </Typography.Text></div>
          )}
          {e.nf_numero && (
            <div><Typography.Text style={{ fontSize: 10, color: '#0a7f4a' }}>🧾 NF {e.nf_numero}</Typography.Text></div>
          )}
        </div>
      ),
    },
    { title: 'Responsável', dataIndex: 'responsavel_email', width: 160, ellipsis: true,
      render: (v) => v || <Typography.Text type="secondary" italic>—</Typography.Text> },
    { title: 'Prevista', dataIndex: 'data_prevista', width: 100, align: 'center',
      render: (v) => <Typography.Text type="secondary" style={{ fontSize: 11 }}>{fmtData(v)}</Typography.Text> },
    { title: 'Valor', dataIndex: 'valor', width: 110, align: 'right',
      render: (v) => v ? <Typography.Text strong>{fmt(v)}</Typography.Text> : '—' },
    { title: 'Parcelas', key: 'parc', width: 130, align: 'center',
      render: (_, e) => (e.parcelas_vinculadas_count ?? 0) > 0 ? (
        <div>
          <Typography.Text strong style={{ color: '#7C3AED' }}>{e.parcelas_vinculadas_count}× vinculada(s)</Typography.Text>
          {(e.parcelas_vinculadas_valor ?? 0) > 0 && (
            <div><Typography.Text type="secondary" style={{ fontSize: 10 }}>{fmt(e.parcelas_vinculadas_valor || 0)}</Typography.Text></div>
          )}
        </div>
      ) : <Typography.Text type="secondary" italic>nenhuma</Typography.Text>,
    },
    { title: 'Status', key: 'status', width: 130, align: 'center',
      render: (_, e) => {
        const se = STATUS_ENTREGAVEL[e.status] || STATUS_ENTREGAVEL.pendente;
        return (
          <div>
            <Tag style={{ background: se.bg, color: se.cor, fontWeight: 600 }}>{se.label}</Tag>
            {e.data_entregue && (
              <div><Typography.Text type="secondary" style={{ fontSize: 9 }}>em {fmtData(e.data_entregue)}</Typography.Text></div>
            )}
          </div>
        );
      },
    },
    { title: 'Ações', key: 'acoes', width: 220, align: 'center',
      render: (_, e) => (
        <Space size={4}>
          {e.status !== 'cancelado' && (
            <Button size="small" icon={<LinkOutlined />}
              onClick={() => setVinculando({ id: e.id, descricao: e.descricao })}
              style={{ borderColor: '#7C3AED', color: '#7C3AED' }}
              title="Vincular parcelas">Parcelas</Button>
          )}
          {e.status === 'pendente' && (
            <>
              <Button size="small" type="primary" icon={<CheckOutlined />}
                onClick={() => marcarEntregue(e)}
                style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}>
                Entregar
              </Button>
              <Button size="small" icon={<EditOutlined />} onClick={() => abrirEditar(e)} title="Editar" />
              <Popconfirm title={`Excluir "${e.descricao}"?`}
                okText="Excluir" okButtonProps={{ danger: true }}
                onConfirm={() => excluir(e)}>
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </>
          )}
          {e.status === 'entregue' && (
            <Popconfirm title='Reverter entrega? Volta para "pendente".'
              onConfirm={() => desmarcarEntregue(e)}>
              <Button size="small" icon={<RollbackOutlined />}
                style={{ borderColor: '#B07500', color: '#B07500' }}>Reverter</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Resumo no topo */}
      <Card size="small" style={{ marginBottom: 14, background: '#FAFBFF' }}>
        <Row gutter={18}>
          <Col xs={12} md={6}>
            <Statistic title="Soma dos entregáveis" value={valorTotal} prefix="R$" precision={0}
              valueStyle={{ fontSize: 18 }}
              formatter={(v) => Number(v).toLocaleString('pt-BR')}
              suffix={<Typography.Text type="secondary" style={{ fontSize: 10 }}>{entregaveis.length} item(s)</Typography.Text>} />
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="Entregue" value={valorEntregue} prefix="R$" precision={0}
              valueStyle={{ fontSize: 18, color: '#0a7f4a' }}
              formatter={(v) => Number(v).toLocaleString('pt-BR')}
              suffix={<Typography.Text type="secondary" style={{ fontSize: 10 }}>{pctEntregue.toFixed(0)}%</Typography.Text>} />
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="A entregar" value={valorPendente} prefix="R$" precision={0}
              valueStyle={{ fontSize: 18, color: '#B07500' }}
              formatter={(v) => Number(v).toLocaleString('pt-BR')} />
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="Cobertura do contrato"
              value={valorContratado > 0 ? ((valorTotal/valorContratado)*100) : 0}
              suffix="%" precision={0}
              valueStyle={{ fontSize: 18, color: valorTotal > valorContratado ? '#B91626' : '#020D7F' }} />
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>contrato: {fmt(valorContratado)}</Typography.Text>
          </Col>
        </Row>
        {valorTotal > valorContratado && (
          <Alert type="error" showIcon style={{ marginTop: 10 }}
            message={`Soma dos entregáveis (${fmt(valorTotal)}) excede o valor contratado (${fmt(valorContratado)}).`} />
        )}
      </Card>

      <Flex align="center" gap={12} style={{ marginBottom: 12 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Cada entregável é um serviço contratado. Após marcar entregue, ele aparece pra Financeiro medir.
        </Typography.Text>
        <div style={{ flex: 1 }} />
        {!criando && (
          <Button type="primary" icon={<PlusOutlined />} onClick={abrirCriar}>Novo entregável</Button>
        )}
      </Flex>

      {criando && (
        <Card size="small" style={{ marginBottom: 14, borderColor: '#0930D0' }}>
          <Form layout="vertical" disabled={salvando}>
            <Row gutter={10}>
              <Col xs={24} md={12}>
                <Form.Item label="Descrição *" required>
                  <Input value={form.descricao}
                    onChange={e => setForm({ ...form, descricao: e.target.value })}
                    placeholder="Ex.: Relatório técnico fase 1 — modelagem hidráulica" />
                </Form.Item>
              </Col>
              <Col xs={12} md={4}>
                <Form.Item label="Valor (R$)">
                  <InputNumber value={form.valor}
                    onChange={(v) => setForm({ ...form, valor: v })}
                    style={{ width: '100%' }} step={100} />
                </Form.Item>
              </Col>
              <Col xs={24} md={5}>
                <Form.Item label="Responsável (e-mail)">
                  <Input type="email" value={form.responsavel_email}
                    onChange={e => setForm({ ...form, responsavel_email: e.target.value })}
                    placeholder="lucas@hydrodataeng.com.br" />
                </Form.Item>
              </Col>
              <Col xs={12} md={3}>
                <Form.Item label="Data prevista">
                  <DatePicker value={form.data_prevista ? dayjs(form.data_prevista) : null}
                    onChange={(d) => setForm({ ...form, data_prevista: d ? d.format('YYYY-MM-DD') : '' })}
                    format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <TextArea value={form.observacoes}
              onChange={e => setForm({ ...form, observacoes: e.target.value })}
              placeholder="Observações (opcional)"
              autoSize={{ minRows: 2, maxRows: 4 }} style={{ marginBottom: 10 }} />
            <Flex justify="end" gap={8}>
              <Button onClick={() => { setCriando(false); setEditando(null); }}>Cancelar</Button>
              <Button type="primary" loading={salvando}
                disabled={!form.descricao.trim()} onClick={salvar}>
                {editando ? 'Salvar' : 'Adicionar'}
              </Button>
            </Flex>
          </Form>
        </Card>
      )}

      {entregaveis.length === 0 ? (
        <Empty description="Nenhum entregável ainda. Adicione os serviços contratados pra começar." />
      ) : (
        <Table size="small" rowKey="id" columns={columns} dataSource={entregaveis}
          pagination={false} scroll={{ x: 'max-content' }} />
      )}

      <Typography.Text type="secondary" italic style={{ fontSize: 10, display: 'block', marginTop: 10 }}>
        💡 Cada entregável pode ser vinculado a 1 ou mais parcelas existentes (via 🔗 Parcelas).
        Marcar como entregue libera automaticamente as parcelas vinculadas pra faturamento.
      </Typography.Text>

      {vinculando && (
        <ModalVincularParcelas
          contratoId={contrato.id}
          entregavelId={vinculando.id}
          entregavelDescricao={vinculando.descricao}
          onClose={() => setVinculando(null)}
          onSucesso={() => { setVinculando(null); onAtualizar(); }}
        />
      )}
    </div>
  );
}

function ModalVincularParcelas({ contratoId, entregavelId, entregavelDescricao, onClose, onSucesso }: {
  contratoId: string; entregavelId: string; entregavelDescricao: string;
  onClose: () => void; onSucesso: () => void;
}) {
  const { message } = AntApp.useApp();
  const [parcelas, setParcelas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [selecionadasIds, setSelecionadasIds] = useState<Set<string>>(new Set());
  const [iniciaisIds, setIniciaisIds] = useState<Set<string>>(new Set());

  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_listar_parcelas_disponiveis', contrato_id: contratoId, entregavel_id: entregavelId },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      const lista = Array.isArray(d) ? d : [];
      setParcelas(lista);
      const vinc = new Set<string>(lista.filter((p: any) => p.vinculada_aqui).map((p: any) => p.id));
      setSelecionadasIds(vinc);
      setIniciaisIds(new Set(vinc));
    } finally { setLoading(false); }
  }

  function toggle(id: string) {
    setSelecionadasIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }

  async function salvar() {
    setSalvando(true);
    try {
      const token = await getToken();
      const headers = { Authorization: token };
      const novos = Array.from(selecionadasIds).filter(id => !iniciaisIds.has(id));
      const removidos = Array.from(iniciaisIds).filter(id => !selecionadasIds.has(id));
      if (novos.length) {
        await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_vincular_parcelas', entregavel_id: entregavelId, parcela_ids: novos },
          { headers });
      }
      for (const pid of removidos) {
        await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_desvincular_parcela', entregavel_id: entregavelId, parcela_id: pid },
          { headers });
      }
      message.success('Vínculos salvos.');
      onSucesso();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  const totalSelecionado = parcelas.filter(p => selecionadasIds.has(p.id))
    .reduce((s, p) => s + (parseFloat(p.valor_previsto) || 0), 0);

  const columns: ColumnsType<any> = [
    { title: '', dataIndex: 'id', width: 40, align: 'center',
      render: (id, p) => {
        const recebida = p.status === 'recebida' || p.status === 'cancelada';
        return <Checkbox checked={selecionadasIds.has(id)} disabled={recebida}
          onChange={() => toggle(id)} onClick={e => e.stopPropagation()} />;
      },
    },
    { title: 'Descrição', key: 'desc',
      render: (_, p) => (
        <div>
          <Typography.Text strong>{p.parcela_titulo || p.descricao || '—'}</Typography.Text>
          {(p.outros_entregaveis || []).length > 0 && (
            <div><Typography.Text style={{ fontSize: 10, color: '#B07500' }}>
              ⚠ Já vinculada a {(p.outros_entregaveis || []).length} outro(s)
            </Typography.Text></div>
          )}
          {p.liberada && (
            <div><Typography.Text style={{ fontSize: 10, color: '#7C3AED' }}>🚀 Já liberada</Typography.Text></div>
          )}
        </div>
      ),
    },
    { title: 'Tipo', dataIndex: 'tipo_parcela', width: 80, align: 'center',
      render: (v) => v || '—' },
    { title: 'Vencimento', dataIndex: 'data_vencimento', width: 100, align: 'center',
      render: (v) => <Typography.Text type="secondary">{fmtData(v)}</Typography.Text> },
    { title: 'Valor', dataIndex: 'valor_previsto', width: 100, align: 'right',
      render: (v) => <Typography.Text strong>{fmt(v || 0)}</Typography.Text> },
    { title: 'Status', dataIndex: 'status', width: 90, align: 'center',
      render: (s) => <Typography.Text type="secondary" style={{ fontSize: 10 }}>{s}</Typography.Text> },
  ];

  return (
    <Modal open onCancel={onClose} width={760}
      title={<Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>🔗 Vincular parcelas</Typography.Title>}
      onOk={salvar} confirmLoading={salvando}
      okText="Salvar vínculos" okButtonProps={{ style: { background: '#7C3AED', borderColor: '#7C3AED' } }}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        Entregável: <strong>{entregavelDescricao}</strong>
      </Typography.Paragraph>
      <Alert type="info" style={{ marginBottom: 12 }}
        message={<Typography.Text style={{ fontSize: 11 }}>
          Selecione as parcelas do contrato que <strong>esta entrega libera pra faturamento</strong>.
          Quando este entregável for marcado como entregue (ou aprovado pelo cliente via BM), as parcelas
          vinculadas viram <strong style={{ color: '#7C3AED' }}>liberadas</strong> automaticamente.
        </Typography.Text>} />

      {loading ? (
        <Flex justify="center" style={{ padding: 30 }}><Spin /></Flex>
      ) : parcelas.length === 0 ? (
        <Empty description="Este contrato não tem parcelas cadastradas. Crie em Financeiro → Contas a Receber." />
      ) : (
        <Table size="small" rowKey="id" columns={columns} dataSource={parcelas}
          pagination={false}
          onRow={(p) => {
            const recebida = p.status === 'recebida' || p.status === 'cancelada';
            return {
              style: {
                cursor: recebida ? 'not-allowed' : 'pointer',
                opacity: recebida ? 0.5 : 1,
                background: selecionadasIds.has(p.id) ? '#F3E8FF' : undefined,
              },
              onClick: () => !recebida && toggle(p.id),
            };
          }} />
      )}

      <div style={{ marginTop: 12, fontSize: 12 }}>
        <Typography.Text strong>{selecionadasIds.size}</Typography.Text> parcela(s) selecionada(s) ·
        total <Typography.Text strong>{fmt(totalSelecionado)}</Typography.Text>
      </div>
    </Modal>
  );
}
